import ImageResizer from './ImageResizer.js';
import DPR from './DPR.js'

export default class Selection {
  constructor(dragCamera, imageDataManager, sizeLabel, imageResizer, requestDraw, doChange) {
    this.maintainAspectRatio = 0;
    this.pasteLargeImage = 'reduceImage';
    this.toggleMode = false;
    this._dragCamera = dragCamera;
    this._imageDataManager = imageDataManager;
    this._requestDraw = requestDraw;
    this._do = doChange;
    this._elRotateIcon = document.querySelector('#rotateIcon');
    this._elPopupLabels = {
      copy: document.querySelector('#popupLabelCopy'),
      paste: document.querySelector('#popupLabelPaste'),
    };
    this._sizeLabel = sizeLabel;
    this._imageResizer = imageResizer;
    this._selectedRect = null;
    this._selectedRectRad = 0;
    this._startSelectedRect = null;
    this._seq = 0;
    this._cursor = '';
    this._isResizerActive = false;
    this._mouseonResizerIdx = -1;
    this._onRotateIcon = false;
    this.__mode = 'rect';
    this._target = 0;
    this._lassoCounts = null;
    this._menu = new SelectionMenu('#selectionMenu', name => this.exec(name));

    this._elRotateIcon.addEventListener('mouseenter', () => {
      this._onRotateIcon = true;
    });
    this._elRotateIcon.addEventListener('mouseleave', () => {
      this._onRotateIcon = false;
    });

    const move = (x, y) => {
      this._selectedRect[0] += x;
      this._selectedRect[1] += y;
      this._imageDataManager.moveSelection(this._selectedRect[0], this._selectedRect[1]);
      this._requestDraw();
    }
    document.addEventListener('keydown', e => {
      if(this._seq === 1) {
        switch(e.code) {
        case 'ArrowUp': move(0, -1); break;
        case 'ArrowDown': move(0, 1); break;
        case 'ArrowLeft': move(-1, 0); break;
        case 'ArrowRight': move(1, 0); break;
        }
      }
    });
  }

  static _RESIZER_CURSOR_RADS = [
    -Math.PI / 4, Math.PI / 4,
    Math.PI / 4, -Math.PI / 4,
  ];

  //--- 静的汎用計算
  static rotate2D(vec, pivot, rad) {
    const sin = Math.sin(rad);
    const cos = Math.cos(rad);
    const [px, py] = pivot;
    let [x, y] = vec;
    [x, y] = [x - px, y - py];
    [x, y] = [
      x * cos - y * sin,
      x * sin + y * cos
    ];
    return [x + px, y + py];
  }
  static rectCenter(rect) {
    const [x, y, w, h] = rect;
    return [x + w / 2, y + h / 2].map(Math.floor);
  }

  //--- 外部から安全にトグルできるように
  get _mode() {
    if(this.toggleMode) {
      switch(this.__mode) {
      case 'rect': return 'lasso';
      case 'lasso': return 'rect';
      }
    }
    return this.__mode;
  }

  //--- 共通取得系
  _worldPtFloored(localX, localY) {
    return this._dragCamera.localPtToWorldPt(localX, localY).map(Math.floor);
  }
  _worldPtReverseRotated(localX, localY) {
    const vec = this._dragCamera.localPtToWorldPt(localX, localY);
    return this._rotateFromPivot(vec, -1);
  }
  _getPivot() {
    const rect = this._startSelectedRect ?? this._selectedRect;
    return Selection.rectCenter(rect);
  }
  _calcSelectedRect(x1, y1, x2, y2) {
    const width = x2 - x1 + 1;
    const height = y2 - y1 + 1;
    if(1 < width || 1 < height) {
      return [x1, y1, width, height];
    } else {
      return null;
    }
  }
  _radFromPivot(localX, localY) {
    const [worldX, worldY] = this._dragCamera.localPtToWorldPt(localX, localY);
    const [pivotX, pivotY] = this._getPivot();
    const gapX = worldX - pivotX;
    const gapY = worldY - pivotY;
    const rad = Math.atan2(gapY, gapX) + Math.PI / 2;
    return rad < 0 ? rad + Math.PI * 2 : rad;
  }
  _rotateFromPivot(vec, radSign = 1) {
    const pivot = this._getPivot();
    return Selection.rotate2D(vec, pivot, this._selectedRectRad * radSign);
  }
  _withinSelectedRect(cursorX, cursorY) {
    if(!this._selectedRect) return false;
    const [worldX, worldY] = this._worldPtReverseRotated(cursorX, cursorY);
    const [x, y, w, h] = this._selectedRect;
    return x <= worldX && worldX < x + w && y <= worldY && worldY < y + h;
  }
  _getResizerRects(x, y, w, h) {
    const sz = (w < h ? w : h) / 4;
    return [
      [x, y, sz, sz],
      [x + w - sz, y, sz, sz],
      [x, y + h - sz, sz, sz],
      [x + w - sz, y + h - sz, sz, sz],
    ];
  }
  _withinResizerRects(cursorX, cursorY) {
    if(!this._selectedRect) return -1;
    const [worldX, worldY] = this._worldPtReverseRotated(cursorX, cursorY);
    const rects = this._getResizerRects(...this._selectedRect);
    for(let i = 0; i < 4; ++i) {
      const [x, y, w, h] = rects[i];
      if(x <= worldX && worldX < x + w && y <= worldY && worldY < y + h) {
        return i;
      }
    }
    return -1;
  }
  _keepWithinCanvas(x, y) {
    const {width, height} = this._imageDataManager;
    x = Math.max(0, Math.min(x, width - 1));
    y = Math.max(0, Math.min(y, height - 1));
    return [x, y];
  }

  //--- モード変更
  setMode(name) {
    this.__mode = name;
    this.finishIfNot();
  }

  //--- モード変更
  setTarget(target) {
    this._target = parseInt(target);
    this.finishIfNot();
  }

  //--- dragToolManagerから呼び出す関数
  start(cursorX, cursorY) {
    switch(this._seq) {
    case 0:  // 未選択
      this._startSelect(cursorX, cursorY);
      this._seq = 1;  // 選択
      break;
    case 1:  // 選択
      if(this._onRotateIcon) {
        this._startRotate(cursorX, cursorY);
        this._seq = 4;  // 回転
      } else if(this._mouseonResizerIdx !== -1) {
        this._startResize(cursorX, cursorY);
        this._seq = 3;  // 拡縮
      } else if(this._withinSelectedRect(cursorX, cursorY)) {
        this._startMove(cursorX, cursorY);
        this._seq = 2;  // 移動
      } else {
        this._do('selection-confirm');
        this._isResizerActive = false;
        this._hideRotateIcon();
        this._seq = 0;  // 未選択
        this.start(cursorX, cursorY);
      }
      this._hideMenu();
      break;
    }
    this._requestDraw();
  }
  cancel() {
    switch(this._seq) {
    case 1:  // 選択
      this._cancelSelect();
      this._seq = 0;
      break;
    case 2:  // 移動
      this._cancelMove();
      this._seq = 1;
      break;
    case 3:  // 拡縮
      this._cancelResize();
      this._seq = 1;
      break;
    case 4:  // 回転
      this._cancelRotate();
      this._seq = 1;
      break;
    }
    this._requestDraw();
  }
  drag(cursorX, cursorY) {
    switch(this._seq) {
    case 1:  // 選択
      this._dragSelect(cursorX, cursorY);
      break;
    case 2:  // 移動
      this._dragMove(cursorX, cursorY);
      break;
    case 3:  // 拡縮
      this._dragResize(cursorX, cursorY);
      break;
    case 4:  // 回転
      this._dragRotate(cursorX, cursorY);
      break;
    }
    this._requestDraw();
  }
  nodrag(cursorX, cursorY) {
    this._mouseonResizerIdx = this._withinResizerRects(cursorX, cursorY);
    if(this._mouseonResizerIdx !== -1) {
      const rad = this._selectedRectRad + Selection._RESIZER_CURSOR_RADS[this._mouseonResizerIdx];
      this._cursor = ImageResizer.getCursorCSSValue(rad);
    } else if(this._withinSelectedRect(cursorX, cursorY)) {
      this._cursor = 'var(--cursor-grab)';
    } else {
      this._cursor = '';
    }
  }
  finish() {
    switch(this._seq) {
    case 1:  // 選択
      this._finishSelect();
      break;
    case 2:  // 移動
      this._finishMove();
      this._seq = 1;
      break;
    case 3:  // 拡縮
      this._finishResize();
      this._seq = 1;
      break;
    case 4:  // 回転
      this._finishRotate();
      this._seq = 1;
      break;
    }
    this._requestDraw();
  }
  getCursor() {
    if(this._cursor) {
      return this._cursor;
    } else if(this._mode === 'lasso') {
      return 'var(--cursor-lasso)';
    } else {
      return 'var(--cursor-selection)';
    }
  }
  isSelecting() {
    return 0 < this._seq;
  }

  //--- 選択
  _startSelect(cursorX, cursorY) {
    switch(this._mode) {
    case 'rect':
      this._startSelectRect(cursorX, cursorY);
      break;
    case 'lasso':
      this._startSelectLasso(cursorX, cursorY);
      break;
    }
    this._selectedRect = null;
    this._selectedRectRad = 0;
  }
  _cancelSelect() {
    this._selectedRect = null;
    this._lassoCounts = null;
    this._hideMenu();
    this._hideRotateIcon();
    this._imageDataManager.cancelSelection();
    this._isResizerActive = false;
    this._sizeLabel.hide();
    this._setResizerSleep(false);
  }
  _dragSelect(cursorX, cursorY) {
    switch(this._mode) {
    case 'rect':
      this._dragSelectRect(cursorX, cursorY);
      break;
    case 'lasso':
      this._dragSelectLasso(cursorX, cursorY);
      break;
    }
  }
  _finishSelect() {
    switch(this._mode) {
    case 'rect':
      this._finishSelectRect();
      break;
    case 'lasso':
      this._finishSelectLasso();
      break;
    }
  }

  //--- 矩形選択専用
  _startSelectRect(cursorX, cursorY) {
    [this._startX, this._startY] = this._worldPtFloored(cursorX, cursorY);
  }
  _dragSelectRect(cursorX, cursorY) {
    const [x, y] = this._worldPtFloored(cursorX, cursorY);
    let x1, y1, x2, y2;
    if(this._startX < x) {
      x1 = this._startX, x2 = x;
    } else {
      x1 = x, x2 = this._startX;
    }
    if(this._startY < y) {
      y1 = this._startY, y2 = y;
    } else {
      y1 = y, y2 = this._startY;
    }
    if(this._selectedRect = this._calcSelectedRect(x1, y1, x2, y2)) {
      this._sizeLabel.show();
      this._sizeLabel.update(cursorX, cursorY, this._selectedRect[2], this._selectedRect[3]);
    } else {
      this._sizeLabel.hide();
    }
  }
  _finishSelectRect() {
    if(this._selectedRect) {
      const [x, y, w, h] = this._selectedRect;
      this._selectedRect = this._calcSelectedRect(
        Math.max(0, x),
        Math.max(0, y),
        Math.min(x + w - 1, this._imageDataManager.width - 1),
        Math.min(y + h - 1, this._imageDataManager.height - 1)
      );
    }
    this._finishSelectBase();
  }
  _finishSelectBase(mask = null) {
    if(this._selectedRect && this._selectedRect[2] > 0 && this._selectedRect[3] > 0) {
      this._orgSelectedRect = this._selectedRect.slice();
      this._showMenu();
      this._showRotateIcon();
      this._isResizerActive = true;
      this._imageDataManager.prepareSelection(...this._selectedRect, mask, this._target);
      this._sizeLabel.hide();
      this._setResizerSleep(this.isSelecting());
    } else {
      this.cancel();
    }
  }

  //--- 自由選択専用
  _startSelectLasso(cursorX, cursorY) {
    const {width, height} = this._imageDataManager;
    const [x, y] = this._keepWithinCanvas(...this._worldPtFloored(cursorX, cursorY));
    this._lassoCounts = Array(width * height);
    this._startX = this._lastLassoDrawnX = x;
    this._startY = this._lastLassoDrawnY = y;
    this._lastLassoY = [,];
  }
  _dragSelectLasso(cursorX, cursorY) {
    const [x, y] = this._keepWithinCanvas(...this._worldPtFloored(cursorX, cursorY));
    this._drawLineOnLassoCounts(x, y);
  }
  _drawLineOnLassoCounts(x2, y2) {
    const {width} = this._imageDataManager;
    const x1 = this._lastLassoDrawnX;
    const y1 = this._lastLassoDrawnY;
    const gapX = x2 - x1;
    const gapY = y2 - y1;
    const set = (x, y) => {
      const idx = y * width + x;
      if(this._lassoCounts[idx] === undefined) {
        this._lassoCounts[idx] = 0;
      }
      if(gapY !== 0) {
        const j = gapY < 0 ? 0 : 1;
        if(this._lastLassoY[j] !== y) {
          ++this._lassoCounts[idx];
          this._lastLassoY[j] = y;
        }
      }
    };
    if(gapX === 0) {
      if(gapY === 0) return;
      const s = Math.sign(gapY);
      for(let y = y1; y * s <= y2 * s; y += s) {
        set(x1, y);
      }
    } else {
      const a = gapY / gapX;
      const b = y2 - a * x2;
      if(Math.abs(a) < 1) {
        const f = x => a * x + b;
        const s = Math.sign(gapX);
        for(let x = x1; x * s <= x2 * s; x += s) {
          set(x, Math.round(f(x)));
        }
      } else {
        const f = y => (y - b) / a;
        const s = Math.sign(gapY);
        for(let y = y1; y * s <= y2 * s; y += s) {
          set(Math.round(f(y)), y);
        }
      }
    }
    this._lastLassoDrawnX = x2;
    this._lastLassoDrawnY = y2;
  }
  _finishSelectLasso() {
    const {width, height} = this._imageDataManager;

    //-- 始点を終点として線を引く
    this._drawLineOnLassoCounts(this._startX, this._startY);
    
    //-- 終点（＝始点）の行の転換点の合計が奇数になるとき補正
    {
      let sum = 0;
      for(let x = 0; x < width; ++x) {
        const count = this._lassoCounts[this._startY * width + x];
        if(count !== undefined) sum += count;
      }
      if(sum % 2 === 1) {
        --this._lassoCounts[this._startY * width + this._startX];
      }
    }

    //-- 転換点を元にマスクを作成
    const mask = new Uint8Array(width * height);
    {
      let x1 = width;
      let y1 = height;
      let x2 = -1;
      let y2 = -1;
      for(let y = 0; y < height; ++y) {
        /*{
          let sum = 0;
          for(let x = 0; x < width; ++x) {
            const idx = y * width + x;
            const count = this._lassoCounts[idx];
            if(count !== undefined) sum += count;
          }
          if(sum % 2 === 1) console.log(sum, y);
        }*/
        let draw = false;
        for(let x = 0; x < width; ++x) {
          const idx = y * width + x;
          const count = this._lassoCounts[idx];
          if(count) {
            if(x < x1) x1 = x;
            if(y < y1) y1 = y;
            if(x2 < x) x2 = x;
            if(y2 < y) y2 = y;
            
            for(let i = 0; i < count; ++i) {
              draw = !draw;
            }
          }
          if(draw || count !== undefined) {
            mask[idx] = 1;
          }
        }
      }
      this._lassoCounts = null;
      this._selectedRect = this._calcSelectedRect(x1, y1, x2, y2);
    }
    this._finishSelectBase(mask);
  }
  _drawLassoMask(ctx) {
    if (!this._lassoCounts) return;
    ctx.save();
    ctx.beginPath();
    ctx.fillStyle = '#888';
    ctx.globalCompositeOperation = 'difference';
    const {width, height} = this._imageDataManager;
    const lsz = this._dragCamera.worldSzToLocalSz(1);
    for(let y = 0; y < height; ++y) {
      for(let x = 0; x < width; ++x) {
        const [lx, ly] = this._dragCamera.worldPtToLocalPt(x, y);
        const counts = this._lassoCounts[y * width + x];
        if(counts !== undefined) {
          ctx.rect(lx, ly, lsz, lsz);
        }
      }
    }
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  //--- 移動
  _startMove(cursorX, cursorY) {
    [this._startX, this._startY] = this._worldPtFloored(cursorX, cursorY);
    this._startRectX = this._selectedRect[0];
    this._startRectY = this._selectedRect[1];
    this._cursor = 'var(--cursor-grabbing)';
    this._isResizerActive = false;
    this._hideRotateIcon();
  }
  _cancelMove() {
    this._selectedRect[0] = this._startRectX;
    this._selectedRect[1] = this._startRectY;
    this._cursor = 'var(--cursor-grab)';
    this._imageDataManager.moveSelection(this._selectedRect[0], this._selectedRect[1]);
    this._showMenu();
    this._showRotateIcon();
    this._isResizerActive = true;
  }
  _dragMove(cursorX, cursorY) {
    const [x, y] = this._worldPtFloored(cursorX, cursorY);
    this._selectedRect[0] = this._startRectX + (x - this._startX);
    this._selectedRect[1] = this._startRectY + (y - this._startY);
    this._imageDataManager.moveSelection(this._selectedRect[0], this._selectedRect[1]);
  }
  _finishMove() {
    this._cursor = 'var(--cursor-grab)';
    this._showMenu();
    this._showRotateIcon();
    this._isResizerActive = true;
    this._setResizerSleep(this.isSelecting());
  }

  //--- 拡縮
  _startResize(cursorX, cursorY) {
    const [cwx, cwy] = this._worldPtReverseRotated(cursorX, cursorY).map(Math.floor);
    const [srx, sry, srw, srh] = this._selectedRect;
    switch(this._mouseonResizerIdx) {
    case 0:  // 左上
      this._resizerOffsetX = cwx - srx;
      this._resizerOffsetY = cwy - sry;
      break;
    case 1:  // 右上
      this._resizerOffsetX = srx + srw - 1 - cwx;
      this._resizerOffsetY = cwy - sry;
      break;
    case 2:  // 左下
      this._resizerOffsetX = cwx - srx;
      this._resizerOffsetY = sry + srh - 1 - cwy;
      break;
    case 3:  // 右下
      this._resizerOffsetX = srx + srw - 1 - cwx;
      this._resizerOffsetY = sry + srh - 1 - cwy;
      break;
    }
    this._startSelectedRect = this._selectedRect.slice();
    this._isResizerActive = false;
    this._sizeLabel.show();
    this._sizeLabel.update(cursorX, cursorY, srw, srh);
    this._hideRotateIcon();
  }
  _cancelResize() {
    this._selectedRect = this._startSelectedRect.slice();
    this._startSelectedRect = null;
    this._imageDataManager.resizeSelection(...this._selectedRect);
    this._showMenu();
    this._showRotateIcon();
    this._isResizerActive = true;
    this._sizeLabel.hide();
  }
  _dragResize(cursorX, cursorY) {
    const [x, y] = this._worldPtReverseRotated(cursorX, cursorY).map(Math.floor);
    const [ox, oy, ow, oh] = this._selectedRect;  // old
    const ox2 = ox + ow;
    const oy2 = oy + oh;
    switch(this._mouseonResizerIdx) {
    case 0:  // 左上
      {
        const x1 = x - this._resizerOffsetX;
        const y1 = y - this._resizerOffsetY;
        const [w, h] = this._maintainAspectRatio(ox + ow - x1, oy + oh - y1);
        this._selectedRect = [ox2 - w, oy2 - h, w, h];
      }
      break;
    case 1:  // 右上
      {
        const x2 = x + this._resizerOffsetX;
        const y1 = y - this._resizerOffsetY;
        const [w, h] = this._maintainAspectRatio(x2 - ox + 1, oy + oh - y1);
        this._selectedRect = [ox, oy2 - h, w, h];
      }
      break;
    case 2:  // 左下
      {
        const x1 = x - this._resizerOffsetX;
        const y2 = y + this._resizerOffsetY;
        const [w, h] = this._maintainAspectRatio(ox + ow - x1, y2 - oy + 1);
        this._selectedRect = [ox2 - w, oy, w, h];
      }
      break;
    case 3:  // 右下
      {
        const x2 = x + this._resizerOffsetX;
        const y2 = y + this._resizerOffsetY;
        const [w, h] = this._maintainAspectRatio(x2 - ox + 1, y2 - oy + 1);
        this._selectedRect = [ox, oy, w, h];
      }
      break;
    }
    this._selectedRect[2] = Math.round(this._selectedRect[2]);
    this._selectedRect[3] = Math.round(this._selectedRect[3]);

    this._imageDataManager.resizeSelection(...this._selectedRect);
    this._sizeLabel.update(cursorX, cursorY, this._selectedRect[2], this._selectedRect[3]);
  }
  _finishResize() {
    {
      const [x, y] = Selection.rectCenter(this._selectedRect);
      const [rx, ry] = Selection.rotate2D([x, y], Selection.rectCenter(this._startSelectedRect), this._selectedRectRad);
      this._selectedRect[0] += rx - x;
      this._selectedRect[1] += ry - y;
      this._imageDataManager.moveSelection(this._selectedRect[0], this._selectedRect[1]);
    }
    this._startSelectedRect = null;
    this._showMenu();
    this._showRotateIcon();
    this._isResizerActive = true;
    this._sizeLabel.hide();
  }
  _maintainAspectRatio(w, h) {
    [w, h] = [w, h].map(v => Math.max(1, v));
    if(!this.maintainAspectRatio) return [w, h];
    const [, , ow, oh] = this._orgSelectedRect;  // org
    const ratio = h / w;
    const orgRatio = oh / ow;
    let nw, nh;
    if(ratio < orgRatio) {
      nw = Math.max(1, Math.round(h / orgRatio));
      nh = h;
    } else {
      nw = w;
      nh = Math.max(1, Math.round(w * orgRatio));
    }
    return [nw, nh];
  }

  //--- 回転
  _startRotate(cursorX, cursorY) {
    this._startSelectedRectRad = this._selectedRectRad;
    this._startCursorRectRad = this._radFromPivot(cursorX, cursorY);
    this._elRotateIcon.classList.add('grabbing');
    this._cursor = 'var(--cursor-grabbing)';
    this._isResizerActive = false;
  }
  _cancelRotate() {
    this._selectedRectRad = this._startSelectedRectRad;
    this._imageDataManager.rotateSelection(this._selectedRectRad);
    this._elRotateIcon.classList.remove('grabbing');
    this._isResizerActive = true;
    this._showMenu();
    this._updateRotateIconPos();
  }
  _dragRotate(cursorX, cursorY) {
    let rad = this._radFromPivot(cursorX, cursorY);
    //console.log(rad.toFixed(2));
    rad += this._startSelectedRectRad - this._startCursorRectRad;
    if(this.maintainAspectRatio) {
      const deg45 = Math.PI / 4;
      rad = deg45 * Math.round(rad / deg45);
    }
    while(rad < 0) rad += 2 * Math.PI;
    this._selectedRectRad = rad;
    this._imageDataManager.rotateSelection(this._selectedRectRad);
  }
  _finishRotate() {
    this._elRotateIcon.classList.remove('grabbing');
    this._showMenu();
    this._isResizerActive = true;
  }

  //--- 通常の流れ以外から状態を確定しリセット
  _forceFinish(clear = false) {
    this._selectedRect = null;
    this._selectedRectRad = 0;
    this._seq = 0;
    this._hideMenu();
    this._hideRotateIcon();
    this._isResizerActive = false;
    this._do('selection-confirm', clear);
    this._requestDraw();
    this._setResizerSleep(this.isSelecting());
  }
  finishIfNot() {
    if(this.isSelecting()) {
      this._forceFinish();
    }
  }

  //--- DOM関連
  _showMenu() {
    this._menu.show();
  }
  _hideMenu() {
    this._menu.hide();
  }
  _showRotateIcon() {
    this._elRotateIcon.classList.remove('hidden');
  }
  _hideRotateIcon() {
    this._elRotateIcon.classList.add('hidden');
  }
  _isMenuShown() {
    return this._menu.isShown();
  }
  _popupLabel(rect, elem) {
    const [x, y, w] = this._dragCamera.worldPtSzToLocalPtSz(...rect);
    elem.style.left = (x + DPR.div(w)) + 'px';
    elem.style.top = DPR.div(y) + 'px';
    elem.classList.add('show');
  }
  _updateMenuPos(...args) {
    if(!this._isMenuShown()) return;
    const [x, y, w, h, pivotX, pivotY] = args.map(v => DPR.div(v));
    const rad = this._selectedRectRad;
    const pivot = [pivotX, pivotY];
    const iconRect = this._elRotateIcon.getBoundingClientRect();
    let bottom = Selection.rotate2D([x + w, y + h], pivot, rad)[1];
    bottom = Math.max(bottom, Selection.rotate2D([x + w, y], pivot, rad)[1]);
    bottom = Math.max(bottom, Selection.rotate2D([x, y], pivot, rad)[1]);
    bottom = Math.max(bottom, Selection.rotate2D([x, y + h], pivot, rad)[1]);
    bottom = Math.max(bottom, iconRect.y + iconRect.height)
    this._menu.move(window.innerWidth - (x + w), bottom);
  }
  _updateRotateIconPos() {
    const [srx, sry, srw] = this._selectedRect;
    const vec = this._rotateFromPivot([srx + srw, sry]);
    const [x, y] = this._dragCamera.worldPtToLocalPt(...vec);
    this._elRotateIcon.style.left = DPR.div(x) + 'px';
    this._elRotateIcon.style.top = DPR.div(y) + 'px';
  }

  //--- 枠線等を描画
  draw(ctx) {
    if(this._selectedRect) {
      const [x, y, w, h] = this._dragCamera.worldPtSzToLocalPtSzD(...this._selectedRect, true);
      const [pivotX, pivotY] = this._dragCamera.worldPtToLocalPtD(...this._getPivot(), true);
      const originX = x - pivotX;
      const originY = y - pivotY;
      /*ctx.save();
      ctx.setLineDash([6, 6]);
      ctx.strokeStyle = 'black';
      ctx.strokeRect(x, y, w, h);
      ctx.lineDashOffset = 6;
      ctx.strokeStyle = 'yellow';
      ctx.strokeRect(x, y, w, h);
      ctx.restore();*/

      ctx.save();
      ctx.translate(pivotX, pivotY);
      ctx.rotate(this._selectedRectRad);
      ctx.strokeStyle = 'white';
      ctx.lineWidth = 3;
      this._drawRects(ctx, originX, originY, w, h);
      ctx.strokeStyle = 'black';
      ctx.lineWidth = 1;
      this._drawRects(ctx, originX, originY, w, h);
      ctx.restore();

      this._updateMenuPos(x, y, w, h, pivotX, pivotY);
      this._updateRotateIconPos();
    }
    this._drawLassoMask(ctx);
  }
  _drawRects(ctx, originX, originY, w, h) {
    ctx.beginPath();
    ctx.rect(originX, originY, w, h);
    if(this._isResizerActive) {
      const rects = this._getResizerRects(originX, originY, w, h);
      ctx.rect(...rects[0]);
      ctx.rect(...rects[1]);
      ctx.rect(...rects[2]);
      ctx.rect(...rects[3]);
    }
    ctx.stroke();
  }

  //--- 選択メニューからの操作
  exec(name) {
    if(!this.isSelecting()) return;
    switch(name) {
    case 'flipX':
      this._imageDataManager.flipSelectionX();
      this._requestDraw();
      break;
    case 'flipY':
      this._imageDataManager.flipSelectionY();
      this._requestDraw();
      break;
    case 'copy':
    case 'cut':
      this._imageDataManager.copySelectionToClipboard(rect => {
        this._popupLabel(rect, this._elPopupLabels.copy);
      });
      if(name === 'copy') break;
      this._forceFinish(true);
      break;
    case 'clear':
      this._forceFinish(true);
      break;
    }
  }

  //--- 全選択
  selectAll(setMode) {
    this.finishIfNot();
    setMode();
    const {width: idw, height: idh} = this._imageDataManager;
    this._selectedRect = [0, 0, idw, idh];
    this._imageDataManager.prepareSelection(...this._selectedRect, null, this._target);
    this._orgSelectedRect = this._selectedRect.slice();
    this._seq = 1;
    this._showMenu();
    this._showRotateIcon();
    this._isResizerActive = true;
    this._setResizerSleep(this.isSelecting());
    this._requestDraw();
  }

  //--- 貼り付け
  async pasteFromClipboard(e) {
    return this._imageDataManager.getBlobFromClipboard(e).then(blob => {
      if(blob) {
        this.finishIfNot();
        document.body.classList.add('dontTouchCuzPaste');
        this._imageDataManager.prepareSelectionFromBlob(blob).then(rect => {
          this._selectedRect = rect;
          this._orgSelectedRect = this._selectedRect.slice();
          this._seq = 1;
          this._showMenu();
          this._showRotateIcon();
          this._isResizerActive = true;
          this._setResizerSleep(this.isSelecting());
          this._popupLabel(rect, this._elPopupLabels.paste);

          const [srx, sry, srw, srh] = this._selectedRect;
          const {width: idw, height: idh} = this._imageDataManager;
          if(idw < srw || idh < srh) {
            switch(this.pasteLargeImage) {
            case 'reduceImage':
              this._pasteReduceImage();
              break;
            case 'enlargeCanvas':
              this._pasteEnlargeCanvas();
              break;
            }
          }
          this._requestDraw();
        }).finally(() => {
          document.body.classList.remove('dontTouchCuzPaste');
        });
      }
    });
  }
  _pasteReduceImage() {
    const [srx, sry, srw, srh] = this._selectedRect;
    const {width: idw, height: idh} = this._imageDataManager;
    if(srw / idw < srh / idh) {
      this._selectedRect[2] = Math.floor(idh * srw / srh);
      this._selectedRect[3] = idh;
      this._selectedRect[0] = Math.floor((idw - this._selectedRect[2]) / 2);
      this._selectedRect[1] = 0;
    } else {
      this._selectedRect[2] = idw;
      this._selectedRect[3] = Math.floor(idw * srh / srw);
      this._selectedRect[0] = 0;
      this._selectedRect[1] = Math.floor((idh - this._selectedRect[3]) / 2);
    }
    this._imageDataManager.resizeSelection(...this._selectedRect);
  }
  _pasteEnlargeCanvas() {
    const [srx, sry, srw, srh] = this._selectedRect;
    const {width: idw, height: idh} = this._imageDataManager;
    let x1, y1, x2, y2;
    if(idw < srw) {
      x1 = srx;
      x2 = srx + srw - 1;
      this._selectedRect[0] = 0;
    } else {
      x1 = 0;
      x2 = idw - 1;
    }
    if(idh < srh) {
      y1 = sry;
      y2 = sry + srh - 1;
      this._selectedRect[1] = 0;
    } else {
      y1 = 0;
      y2 = idh - 1;
    }
    this._do('selection-resize-canvas', x1, y1, x2, y2);
    this._imageDataManager.moveSelection(this._selectedRect[0], this._selectedRect[1]);
  }
  _setResizerSleep(yes) {
    if(yes) {
      this._imageResizer.sleep();
    } else {
      this._imageResizer.wake();
    }
  }
}

class SelectionMenu {
  constructor(selector, exec) {
    this._elMenu = document.querySelector(selector);

    this._elMenu.addEventListener('click', e => {
      if(e.target.classList.contains('button')) {
        exec(e.target.dataset.name);
      }
    });
  }
  show() {
    this._elMenu.classList.remove('hidden');
  }
  hide() {
    this._elMenu.classList.add('hidden');
  }
  isShown() {
    return !this._elMenu.classList.contains('hidden');
  }
  move(right, bottom) {
    this._elMenu.style.right = right + 'px';
    this._elMenu.style.top = bottom + 'px';
  }
}
